/**
 * /prioritization-frameworks — SEO page for the prioritization cluster:
 * "moscow prioritization", "prioritization", "rice framework/method",
 * "wsjf prioritization", "weighted decision matrix" (≈850 current
 * traffic, KD 2–15, clean strong fit). One guide covers the frameworks
 * PMs actually use, with worked numbers, then the Draftlize turn: a
 * prioritization score is a decision with inputs (reach, impact, effort,
 * confidence); when an input changes the ranking should change, but in a
 * spreadsheet it just rots. Draftlize tracks the inputs so a
 * re-prioritization propagates instead of going stale.
 *
 * English-only + inline. Chinese variant in PrioritizationFrameworksZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function PrioritizationFrameworks() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/prioritization-frameworks"
      documentTitle="Prioritization Frameworks: RICE, MoSCoW, WSJF · Draftlize"
      documentDescription="The prioritization frameworks PMs actually use — RICE, MoSCoW, WSJF, Kano, weighted scoring — with worked examples, when each fits, and a copyable decision matrix."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Prioritization</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 60px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Prioritization frameworks,<br/>
            <em>and when each one earns its keep.</em>
          </h1>
          <p className="lede">
            Every prioritization framework exists to turn an argument into a number you can defend. RICE, MoSCoW, WSJF, Kano, a weighted scoring model — they&apos;re different lenses on the same question: <em>what should we do next, and how sure are we?</em> This page gives you each one with the actual formula and a worked example, then a straight table of which to reach for when. And then the part the templates skip — a score is only true until one of its inputs changes, and in a spreadsheet nothing tells you when that happens.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why prioritize <em>(and why scores rot).</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            Prioritization is just making the trade-off explicit instead of implicit. You have more good ideas than capacity; a framework forces you to say <em>why</em> this one beats that one in terms you can repeat to a skeptical stakeholder. The number isn&apos;t the point — the agreed <em>inputs</em> behind the number are. A RICE score of 42 means nothing; &quot;reach 8k, impact 2, confidence 80%, effort 3 person-months&quot; means something, and it&apos;s arguable in the right way.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            Which is exactly why prioritization decays. Those inputs are assumptions about a moving world. Reach was estimated before the segment shifted; effort was a guess before the spike; confidence was high before the experiment came back flat. The ranking you defended in January is quietly wrong by March — and the spreadsheet looks just as confident as the day you filled it in. A framework gives you a good decision; nothing in a spreadsheet keeps it good.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The frameworks</span>
            <h2 className="serif">Five lenses, <em>with the actual math.</em></h2>
          </div>
          <p>Each with its formula, a one-line worked example, and the situation it&apos;s built for. None is &quot;best&quot; — they trade rigor for speed differently.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'RICE', hint: 'Reach·Impact·Confidence÷Effort', body: 'Score = (Reach × Impact × Confidence) ÷ Effort. E.g. (8,000 users × 2 × 0.8) ÷ 3 = 4,267. Best when you have rough quantitative inputs and want a comparable score across very different features. The confidence term is the honest part — it docks ideas you can\'t back up.' },
            { k: 'MoSCoW', hint: 'Must·Should·Could·Won\'t', body: 'Sort every item into Must-have, Should-have, Could-have, Won\'t-have (this time). No math — it\'s a fast alignment tool, ideal for scoping a release with stakeholders in a room. Its discipline is the "Won\'t" bucket: naming what you\'re deliberately not doing prevents silent scope creep.' },
            { k: 'WSJF', hint: 'Cost of Delay ÷ Job Size', body: 'Weighted Shortest Job First = Cost of Delay ÷ Job Size, from SAFe/agile. Surfaces the small, time-sensitive things that a pure impact score buries. Best when sequencing a backlog where timing matters — a fix worth a lot now and little later scores high.' },
            { k: 'Kano model', hint: 'Basic·Performance·Delight', body: 'Classifies features by how they affect satisfaction: basics (absence hurts, presence is invisible), performance (more is better), delighters (unexpected upside). Best for balancing a roadmap so you don\'t over-invest in delight while a basic expectation is missing.' },
            { k: 'Weighted scoring', hint: 'Custom criteria × weights', body: 'A decision matrix: pick criteria (impact, effort, strategic fit, risk), weight them, score each option, sum. The most flexible — you define what matters. Best for a one-off, high-stakes choice between a few options where RICE\'s fixed inputs don\'t fit. (This is the "weighted decision matrix".)' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Pick one</span>
            <h2 className="serif">Which framework, <em>when.</em></h2>
          </div>
          <p>Match the tool to the decision, not the other way around. A quick guide.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>Your situation</th>
              <th>Reach for</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Comparing many features with rough data</td><td className="yes">RICE</td><td>One comparable score, confidence-adjusted</td></tr>
            <tr><td>Scoping a release with stakeholders</td><td className="yes">MoSCoW</td><td>Fast alignment, forces a &quot;Won&apos;t&quot; list</td></tr>
            <tr><td>Sequencing where timing matters</td><td className="yes">WSJF</td><td>Surfaces small, urgent, cheap wins</td></tr>
            <tr><td>Balancing basics vs delight</td><td className="partial">Kano</td><td>Classifies by satisfaction effect</td></tr>
            <tr><td>One high-stakes choice, few options</td><td className="yes">Weighted matrix</td><td>You define the criteria and weights</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The score is a decision. <em>Treat it like one.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            A prioritization score isn&apos;t a fact — it&apos;s a decision built from inputs. The framework is only as live as those inputs, and a spreadsheet has no idea when one of them moved. This is the narrow thing Draftlize does.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Inputs become cards, not cells</h3>
            <p>Reach, impact, confidence, effort — each is an assumption that lives somewhere. In Draftlize they&apos;re cards: a reach estimate cites the analysis behind it, a confidence level cites the experiment that set it. The score links to its inputs instead of hard-coding their values in a cell nobody revisits.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">An input changes, the ranking flags stale</h3>
            <p>The experiment comes back flat, so confidence drops. The card that held the old confidence changes, and every score that depended on it auto-flags stale — so a re-prioritization is triggered by the world moving, not by someone happening to re-open the sheet. The ranking stays honest because the substrate, not your memory, tracks what it rests on.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent re-scores against the truth</h3>
            <p>Ask Claude Code or Cursor to re-rank the backlog and it reads the current inputs over MCP — the live reach, the updated confidence — not the values you typed last quarter. The framework becomes something that re-runs against reality instead of a snapshot you defend long after it stopped being true.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A prioritization framework gives you a good decision. Nothing in a spreadsheet keeps it good when the numbers underneath it move.
        </blockquote>
        <cite>Keep your framework. Track the inputs it rests on so the ranking can&apos;t quietly rot.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the easiest prioritization framework to start with?', a: 'MoSCoW. It needs no math and no data — you sort items into Must, Should, Could, and Won\'t-have. It is the fastest way to get a room aligned on the scope of a release, and you can graduate to RICE once you have rough quantitative inputs.' },
            { q: 'RICE vs WSJF — what is the difference?', a: 'RICE optimizes for overall value per unit of effort and is confidence-adjusted, so it is good for comparing many features broadly. WSJF (Cost of Delay ÷ Job Size) optimizes for sequencing when timing matters, so it surfaces small, urgent, cheap wins that RICE would bury. Use RICE to choose, WSJF to order.' },
            { q: 'How do I score impact or confidence objectively?', a: 'You do not — they are estimates, and pretending otherwise is the trap. The value is in writing the assumption behind each number so it can be challenged and revisited. A confidence of 80% should cite why; when the why changes, the score should change with it.' },
            { q: 'How often should I re-prioritize?', a: 'When an input changes, not on a fixed calendar. A monthly re-score is a workaround for not knowing which assumptions moved. If your inputs are tracked, re-prioritization is triggered by the thing that actually invalidated the old ranking.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Keep your ranking honest <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Score with whatever framework you like. Put the inputs in Draftlize as cards, and let a changed assumption flag every ranking that depended on it — so your priorities re-rank when the world moves, not when you remember to.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
